import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"

const API_URL = process.env.REACT_APP_API_URL

export const registerStudent = createAsyncThunk(
  "students/registerStudent",
  async (studentData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      }

      if (studentData instanceof FormData) {
        config.headers["Content-Type"] = "multipart/form-data"
      } else {
        config.headers["Content-Type"] = "application/json"
      }

      const response = await axios.post(`${API_URL}/auth/student/register`, studentData, config)
      return response.data?.data ?? response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Registration failed")
    }
  },
)

export const fetchStudents = createAsyncThunk("students/fetchStudents", async (_, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/students`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data?.data ?? response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const fetchStudentById = createAsyncThunk("students/fetchStudentById", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.get(`${API_URL}/students/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.data?.data ?? response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const fetchStudentByEmail = createAsyncThunk(
  "students/fetchStudentByEmail",
  async (email, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_URL}/students/email/${email}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      return response.data?.data ?? response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

export const updateStudent = createAsyncThunk("students/updateStudent", async ({ id, data }, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    const response = await axios.put(`${API_URL}/students/${id}`, data, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
    })
    return response.data?.data ?? response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const updateAdmissionStatus = createAsyncThunk(
  "students/updateAdmissionStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.patch(
        `${API_URL}/students/${id}/admission-status`,
        { admission_status: status },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      return response.data?.student ?? response.data?.data ?? response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

export const deleteStudent = createAsyncThunk("students/deleteStudent", async (id, { rejectWithValue }) => {
  try {
    const token = localStorage.getItem("token")
    await axios.delete(`${API_URL}/students/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return id
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message)
  }
})

export const setHodVerification = createAsyncThunk(
  "students/setHodVerification",
  async ({ studentId, received }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const status = received ? "COMPLETED" : "REJECTED"
      const notes = received ? "AMOUNT_RECEIVED" : "AMOUNT_NOT_RECEIVED"
      const res = await axios.put(
        `${API_URL}/enrollment/${studentId}/workflow/VERIFICATION`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      // Return minimal payload to update store
      return {
        student_id: Number(studentId),
        hod_verification_step_status: status,
        hod_verification_status: received ? "RECEIVED" : "NOT_RECEIVED",
        hod_verification_notes: notes,
        hod_verified: received,
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

export const setAccountVerification = createAsyncThunk(
  "students/setAccountVerification",
  async ({ studentId, received }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token")
      const status = received ? "COMPLETED" : "REJECTED"
      const notes = received ? "AMOUNT_RECEIVED" : "AMOUNT_NOT_RECEIVED"
      await axios.put(
        `${API_URL}/enrollment/${studentId}/workflow/FEES_PAYMENT`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      return {
        student_id: Number(studentId),
        accounts_verification_step_status: status,
        accounts_verification_status: received ? "RECEIVED" : "NOT_RECEIVED",
        accounts_verification_notes: notes,
        accounts_verified: received,
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message)
    }
  },
)

const studentSlice = createSlice({
  name: "students",
  initialState: {
    students: [],
    currentStudent: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // registerStudent
      .addCase(registerStudent.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(registerStudent.fulfilled, (state, action) => {
        state.loading = false
        state.students.push(action.payload)
      })
      .addCase(registerStudent.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // fetchStudents (admin list)
      .addCase(fetchStudents.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.loading = false
        state.students = action.payload
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // fetchStudentById
      .addCase(fetchStudentById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchStudentById.fulfilled, (state, action) => {
        state.loading = false
        state.currentStudent = action.payload
        state.error = null
      })
      .addCase(fetchStudentById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // fetchStudentByEmail
      .addCase(fetchStudentByEmail.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchStudentByEmail.fulfilled, (state, action) => {
        state.loading = false
        state.currentStudent = action.payload
        state.error = null
      })
      .addCase(fetchStudentByEmail.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // updateStudent
      .addCase(updateStudent.fulfilled, (state, action) => {
        const index = state.students.findIndex((s) => s.student_id === action.payload.student_id)
        if (index !== -1) {
          state.students[index] = action.payload
        }
        if (state.currentStudent && state.currentStudent.student_id === action.payload.student_id) {
          state.currentStudent = action.payload
        }
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.error = action.payload
      })

      // setHodVerification
      .addCase(setHodVerification.fulfilled, (state, action) => {
        const {
          student_id,
          hod_verification_step_status,
          hod_verification_status,
          hod_verification_notes,
          hod_verified,
        } = action.payload || {}
        const idx = state.students.findIndex((s) => s?.student_id === student_id)
        if (idx !== -1) {
          state.students[idx] = {
            ...state.students[idx],
            hod_verification_step_status,
            hod_verification_status,
            hod_verification_notes,
            hod_verified,
          }
        }
        if (state.currentStudent && state.currentStudent.student_id === student_id) {
          state.currentStudent = {
            ...state.currentStudent,
            hod_verification_step_status,
            hod_verification_status,
            hod_verification_notes,
            hod_verified,
          }
        }
      })
      .addCase(setHodVerification.rejected, (state, action) => {
        state.error = action.payload
      })

      // setAccountVerification
      .addCase(setAccountVerification.fulfilled, (state, action) => {
        const update = action.payload
        state.students = state.students.map((s) =>
          Number(s.student_id) === Number(update.student_id) ? { ...s, ...update } : s,
        )
        if (state.currentStudent && Number(state.currentStudent.student_id) === Number(update.student_id)) {
          state.currentStudent = { ...state.currentStudent, ...update }
        }
      })
      .addCase(setAccountVerification.rejected, (state, action) => {
        state.error = action.payload
      })

      // updateAdmissionStatus
      .addCase(updateAdmissionStatus.fulfilled, (state, action) => {
        if (!action.payload) {
          console.warn("updateAdmissionStatus fulfilled with undefined payload")
          return
        }
        const payload = action.payload
        const id = payload.student_id ?? payload.id
        const index = state.students.findIndex((s) => s?.student_id === id)
        if (index !== -1) {
          state.students[index] = payload
        } else {
          console.warn("Student not found in state when updating admission status", id)
        }
        if (state.currentStudent && (state.currentStudent.student_id === id || state.currentStudent.id === id)) {
          state.currentStudent = payload
        }
      })
      .addCase(updateAdmissionStatus.rejected, (state, action) => {
        state.error = action.payload
      })

      // deleteStudent
      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.students = state.students.filter((s) => s.student_id !== action.payload)
      })
      .addCase(deleteStudent.rejected, (state, action) => {
        state.error = action.payload
      })
  },
})

export const { clearError } = studentSlice.actions
export default studentSlice.reducer
